import { motion } from "motion/react";
import { Link } from "react-router";
import { Search, ArrowRight, Calendar, Clock, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { subscribe } from '../../apihandlers';
import { articles } from "../data/articles";

const categories = ["All", "Strategy", "Technology", "Design", "Security", "Innovation", "Growth"];

export function ArticlesPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredArticles = articles.filter(article => {
    const matchesCategory = activeCategory === "All" || article.category === activeCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const form = e.target as HTMLFormElement;
    const emailInput = form.elements.namedItem('email') as HTMLInputElement;
    
    try {
      await subscribe({
        email: emailInput.value,
      });
      setIsSubmitting(false);
      toast.success("Subscribed successfully!", {
        description: "You've been added to our newsletter.",
        duration: 5000,
      });
    } catch (e: any) {
      setIsSubmitting(false);
      console.log(e);
      toast.error("Something went wrong, please try again later!");
    }

    // Reset form
    form.reset();
  };

  return (
    <div className="pt-20">
      {/* Hero Header */}
      <section className="bg-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-[#FD5750] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[30%] h-[30%] bg-blue-500 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#FD5750] font-bold tracking-widest uppercase mb-4 block">Knowledge Hub</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Insights & <span className="text-[#FD5750]">Intelligence</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Expert perspectives on digital transformation, technology trends, and strategic business growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter & Search Bar */}
      <section className="py-10 bg-white border-b border-slate-100 sticky top-20 z-40 shadow-sm">
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 no-scrollbar">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  activeCategory === cat 
                    ? "bg-[#FD5750] text-white" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          
          <div className="relative w-full md:w-80">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-[#FD5750] focus:ring-1 focus:ring-[#FD5750] transition-all"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-slate-50 min-h-[50vh]">
        <div className="container mx-auto px-6">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group flex flex-col h-full border border-slate-100"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={article.image} 
                      alt={article.title} 
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-[#FD5750] uppercase tracking-wide">
                      {article.category}
                    </div>
                  </div>
                  
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        {article.date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        {article.readTime}
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-[#FD5750] transition-colors leading-tight">
                      <Link to={`/articles/${article.slug}`}>
                        {article.title}
                      </Link>
                    </h3>
                    
                    <p className="text-slate-600 text-sm mb-6 flex-grow line-clamp-3">
                      {article.excerpt}
                    </p>
                    
                    <div className="pt-4 border-t border-slate-100 mt-auto">
                      <Link 
                        to={`/articles/${article.slug}`}
                        className="flex items-center gap-2 text-sm font-bold text-slate-900 group-hover:text-[#FD5750] transition-colors"
                      >
                        Read Article <ArrowRight size={16} />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                <Search size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No articles found</h3>
              <p className="text-slate-500">Try adjusting your search or category filter.</p>
              <button 
                onClick={() => { setSearchQuery(""); setActiveCategory("All"); }}
                className="mt-4 text-[#FD5750] font-semibold hover:underline"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Stay Ahead of the Curve
          </h2>
          <p className="text-slate-600 mb-8">
            Get the latest insights on technology, strategy, and innovation delivered directly to your inbox. No spam, just value.
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" onSubmit={handleSubscribe}>
            <input 
              type="email" 
              name="email"
              required
              placeholder="Your email address" 
              className="flex-1 px-5 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-[#FD5750] focus:ring-1 focus:ring-[#FD5750]"
            />
            <button 
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-slate-900 text-white font-bold rounded-lg hover:bg-[#FD5750] transition-colors shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed min-w-[120px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Subscribing...
                </>
              ) : (
                "Subscribe"
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
